import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { MAX_FOLDER_NAME_LENGTH, EntityTypeDTO } from '../../DocumentVault.meta';
import * as yup from 'yup';

export const entityNameValidation = yup.object().shape({
  name: yup
    .string()
    .required('Name is required')
    .matches(
      /^[a-zA-Z0-9-!.*_'() ]+$/,
      "Names must only include letters, numbers, and the special characters: ! - _ . * ' ( )",
    ),
});

interface EntityNameFields {
  name: string;
}

export interface EntityNameModalProps {
  mode: 'create' | 'rename';
  entityType: EntityTypeDTO;
  initialName?: string;
  extensionSuffix?: string;
  isLoading: boolean;
  error: Error | null;
  onSubmit(name: string): void;
  onClose(): void;
}

export const EntityNameModal: React.FC<EntityNameModalProps> = ({
  mode,
  entityType,
  initialName = '',
  extensionSuffix = '',
  isLoading,
  error,
  onSubmit,
  onClose,
}) => {
  const isFolder = entityType === EntityTypeDTO.Folder;
  const isRenaming = mode === 'rename';
  const hasExtension = extensionSuffix.length > 0;

  const title = isRenaming ? (isFolder ? 'Rename Folder' : 'Rename File') : 'New Folder';
  const placeholder = isRenaming ? (isFolder ? 'Enter folder name' : 'Enter file name') : 'New Folder Name';
  const submitButtonText = isRenaming ? 'Rename' : 'Create';

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<EntityNameFields>({
    defaultValues: {
      name: initialName,
    },
    resolver: yupResolver(entityNameValidation),
  });

  const handleFormSubmit = ({ name }: EntityNameFields) => {
    onSubmit(name);
  };

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>{title}</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody>
          {error && <Alert message={error.message} variant="error" my="16" />}

          <Box display="flex" alignItems="center" mt="8">
            <Box flex="1">
              <Input
                {...register('name')}
                type="text"
                placeholder={placeholder}
                formVariant={errors.name ? 'error' : undefined}
                helperText={errors.name ? errors.name.message : ''}
                sizeVariant="large"
                disabled={isLoading}
                aria-label={isFolder ? 'Folder name' : 'File name'}
                maxLength={MAX_FOLDER_NAME_LENGTH}
              />
            </Box>
            {hasExtension && (
              <Text color="neutral.600" ml="8" fontWeight={500}>
                {extensionSuffix}
              </Text>
            )}
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" ml="12" isLoading={isLoading}>
            {submitButtonText}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
